/**
 * 606 Meta5 AI Trading - AI Log Tab
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Bot, RefreshCw } from 'lucide-react';
import { useTrading } from '../../lib/trading-i18n';
import { fetchAILogs } from '../../lib/trading-api';
import TerminalLog from '../../components/trading/TerminalLog';
import { Button } from '../../components/ui/button';

const FILTERS = [
  { key: null, label: 'filterAll' },
  { key: 'DECISION', label: 'filterDecision' },
  { key: 'TRADE', label: 'filterTrade' },
  { key: 'WARN', label: 'filterWarn' },
  { key: 'ERROR', label: 'filterError' },
  { key: 'INFO', label: 'filterInfo' },
];

const AILogTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);

  const loadLogs = useCallback(async () => {
    try {
      const data = await fetchAILogs(200, filter);
      setLogs(data.data || []);
    } catch (err) {
      console.error('Failed to load AI logs:', err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    loadLogs();
    const interval = setInterval(loadLogs, 5000);
    return () => clearInterval(interval);
  }, [loadLogs]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Bot className="h-5 w-5 text-[#d4af37]" />
          {t.aiLog}
        </h3>
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <Button
              key={f.key || 'all'}
              size="sm"
              variant={filter === f.key ? 'default' : 'outline'}
              onClick={() => setFilter(f.key)}
              className={filter === f.key 
                ? 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/30' 
                : 'bg-white/5 text-[#71717A] border-white/10 hover:text-white'
              }
              data-testid={`filter-${f.key || 'all'}`}
            >
              {t[f.label]}
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={loadLogs}
            className="text-[#71717A] hover:text-white"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Terminal */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-4"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <TerminalLog logs={logs} maxHeight={500} />
        )}
      </motion.div>
    </div>
  );
};

export default AILogTab;
